import {
  CreateImagePowerPointParams,
  CreateMultiImagePowerPointParams,
  GetExportImagePowerPointParams,
  GetExportMultiImagePowerPointParams,
} from './Model';
import { saveFile } from './SaveFile';
import { getChartExportImage, getMultiChartExportImage } from './ExportChartImage';
import { getChartExportPowerPoint, getMultiChartExportPowerPoint } from './ExportChartPowerPoint';
import { AVERAGE_SELLING_PRICE, EXPLORE_SEGMENTS_CARD_TITLE } from '@pages/Utilities';
import { svgToPng } from '@utils/SvgToPng';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { ImageURLData } from '@ecosystem/export-utils';
import { PageName } from '@utils/pageConfig/Types';
import { EXPLORE_TRENDS } from '@utils/StringConstants';

export const processExportedImages = async (exportImages: CapturedImage[] = []) => {
  return Promise.all(
    exportImages.map(async (image) => ({
      ...image,
      dataUrl: await svgToPng(image),
    })),
  );
};

export const createPowerPoint = async (methodParam: CreateImagePowerPointParams) => {
  const exportPowerpointData = await methodParam.invokeExport();
  const exportImage = methodParam.exportImage;
  const exportedPngImage = exportImage ? await svgToPng(exportImage) : null;

  if (!exportPowerpointData && !exportImage) return;

  const params: GetExportImagePowerPointParams = {
    ...methodParam,
    imageData: exportPowerpointData ?? { ...exportImage, dataUrl: exportedPngImage },
  };

  const exportData = getChartExportPowerPoint(params);
  const exportFile = await methodParam.exportFn(exportData);
  saveFile(exportFile);
};

export const createMultiImagePowerPoint = async (methodParam: CreateMultiImagePowerPointParams) => {
  const { exportImage, multipleChartsExport } = methodParam;

  if (Array.isArray(exportImage) && exportImage.length) {
    const exportedPngImages = await processExportedImages(exportImage);
    const params: GetExportMultiImagePowerPointParams = {
      ...methodParam,
      imageData: exportedPngImages,
    };

    const exportData = getMultiChartExportPowerPoint(params);
    const exportFile = await methodParam.exportFn(exportData);
    return saveFile(exportFile);
  }

  if (Array.isArray(methodParam?.multipleChartsExport) && (exportImage?.length ?? 0) === 0) {
    const invokeExportsResultList = await Promise.all(multipleChartsExport?.map((exp) => exp.invokeExport?.()));
    const imageDataList = invokeExportsResultList.filter(Boolean);

    const params: GetExportMultiImagePowerPointParams = {
      ...methodParam,
      imageData: imageDataList,
    };

    const exportData = getMultiChartExportPowerPoint(params);
    const exportFile = await methodParam.exportFn(exportData);
    return saveFile(exportFile);
  }
};

export const createImage = async (methodParam: CreateImagePowerPointParams, title: string) => {
  const exportImageData = await methodParam.invokeExport();
  if (!exportImageData && !methodParam.exportImage) return;
  let params: GetExportImagePowerPointParams = {
    ...methodParam,
    imageData: exportImageData ?? methodParam.exportImage,
    title,
    subTitle: title === AVERAGE_SELLING_PRICE ? '' : title,
  };

  if (methodParam.pageName === PageName.EXPLORE && methodParam.title.includes(EXPLORE_SEGMENTS_CARD_TITLE)) {
    params.imageData = exportImageData ?? (await processExportedImages([methodParam.exportImage]));
  }

  const exportData = getChartExportImage(params);
  const exportFile = await methodParam.exportFn(exportData);
  saveFile(exportFile);
};

export const createMultiChartImage = async (methodParams: CreateMultiImagePowerPointParams) => {
  let imageDataList: (ImageURLData | undefined)[] = [];

  if (Array.isArray(methodParams?.multipleChartsExport) && methodParams.multipleChartsExport.length) {
    const invokeExportsResultList = await Promise.all(
      methodParams.multipleChartsExport.map((exp) => exp.invokeExport?.()),
    );
    imageDataList = invokeExportsResultList.filter(Boolean);

    if (!imageDataList.length) {
      console.warn('No export image data retrieved from multipleChartsExport');
    }
  }

  let params: GetExportMultiImagePowerPointParams = {
    ...methodParams,
    imageData: imageDataList.length ? imageDataList : methodParams.exportImage,
  };

  if (methodParams.pageName === PageName.EXPLORE && methodParams.chartTitles.includes(EXPLORE_TRENDS)) {
    params.imageData = imageDataList.length ? imageDataList : await processExportedImages(methodParams.exportImage);
  }
  const exportData = getMultiChartExportImage(params);
  const exportFile = await methodParams.exportFn(exportData);
  saveFile(exportFile);
};
